import React from 'react';
import { Typography, Card, Space, Tag } from 'antd';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Array of colors for multiple lines
const LINE_COLORS = [
  '#1890ff', // blue
  '#52c41a', // green
  '#f5222d', // red
  '#fa8c16', // orange
  '#722ed1', // purple
  '#eb2f96', // pink
  '#faad14', // yellow
  '#13c2c2', // cyan
  '#2f54eb', // geekblue
  '#a0d911'  // lime
];

// Component to display variance data from multiple dates in a single chart
const OTRMultiTripChart = ({ dateTrips }) => {
  // Custom tooltip to show detailed stop information
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card size="small">
          <Space direction="vertical" size="small">
            <Text strong>Stop {label}</Text>
            {payload.map((entry, index) => {
              const data = entry.payload[entry.dataKey];
              const variance = data?.variance;
              // Skip entries with no variance
              if (variance === undefined || variance === null) return null;
              
              // Determine status color based on variance
              let color = 'green';
              let statusText = 'On Time';
              let icon = <CheckCircleOutlined />;
              
              if (variance < -2) {
                color = 'red';
                statusText = 'Early';
                icon = <WarningOutlined />;
              } else if (variance > 5) {
                color = 'blue';
                statusText = 'Late';
                icon = <CloseCircleOutlined />;
              }
              
              return (
                <div key={index}>
                  <Text strong style={{ color: entry.color }}>{data.date}</Text>
                  <div>
                    <Text>
                      Variance: 
                      <Text style={{ color }}>
                        {variance > 0 ? ' +' : ' '}{variance.toFixed(1)} min
                      </Text>
                    </Text>
                  </div>
                  <Tag color={color} icon={icon}>{statusText}</Tag>
                </div>
              );
            })}
          </Space>
        </Card>
      );
    }
    
    return null;
  };
  
  // Find the maximum sequence number across all dates to ensure consistent X-axis
  const maxSequence = React.useMemo(() => {
    let max = 0;
    for (const dateTrip of dateTrips) {
      const chartData = dateTrip.chartData || [];
      for (const stop of chartData) {
        if (stop.sequence > max) {
          max = stop.sequence;
        }
      }
    }
    return max;
  }, [dateTrips]);
  
  // Prepare data for the chart - make sure all dates have the same X-axis points
  const chartData = React.useMemo(() => {
    const data = [];
    
    // Initialize data points for each sequence
    for (let i = 1; i <= maxSequence; i++) {
      const point = { sequence: i };
      // Initialize empty objects for each date
      dateTrips.forEach((dateTrip, index) => {
        point[`date${index}`] = { date: dateTrip.date };
      });
      data.push(point);
    }
    
    // Fill in variance data for each date
    dateTrips.forEach((dateTrip, dateIndex) => {
      const chartData = dateTrip.chartData || [];
      chartData.forEach(stop => {
        const index = stop.sequence - 1;
        if (index >= 0 && index < data.length) {
          data[index][`date${dateIndex}`] = {
            ...stop,
            date: dateTrip.date
          };
        }
      });
    });
    
    return data;
  }, [dateTrips, maxSequence]);
  
  return (
    <div style={{ width: '100%', height: 400 }}>
      <ResponsiveContainer>
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="sequence" 
            label={{ value: 'Stop Sequence', position: 'insideBottom', offset: -10 }}
          />
          <YAxis 
            label={{ 
              value: 'Variance (minutes)', 
              angle: -90, 
              position: 'insideLeft',
              offset: -10
            }}
          />
          <RechartsTooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* On-time reference lines */}
          <ReferenceLine 
            y={0} 
            stroke="#666" 
            strokeDasharray="3 3" 
            label={{ value: 'On Time', position: 'right', fill: '#666' }}
          />
          <ReferenceLine 
            y={5} 
            stroke="#1890ff" 
            strokeDasharray="3 3" 
            label={{ value: 'Late Threshold', position: 'right', fill: '#1890ff' }}
          />
          <ReferenceLine 
            y={-2} 
            stroke="#f5222d" 
            strokeDasharray="3 3" 
            label={{ value: 'Early Threshold', position: 'right', fill: '#f5222d' }}
          />
          
          {/* Add a line for each date */}
          {dateTrips.map((dateTrip, index) => (
            <Line
              key={dateTrip.dateId || index}
              name={dateTrip.date}
              type="monotone"
              dataKey={`date${index}.variance`}
              stroke={LINE_COLORS[index % LINE_COLORS.length]}
              strokeWidth={2}
              connectNulls={true}
              activeDot={{ r: 8 }}
              dot={{ 
                r: 6, 
                strokeWidth: 2,
                fill: 'white',
                stroke: (entry) => {
                  const variance = entry?.[`date${index}`]?.variance;
                  if (variance === undefined || variance === null) return '#666';
                  if (variance < -2) return '#f5222d'; // early - red
                  if (variance > 5) return '#1890ff';  // late - blue
                  return '#52c41a'; // on time - green
                }
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OTRMultiTripChart; 